import React, { useRef, type ReactNode } from 'react';
import { HilEngine, FetchHilSubmitClient, DevMockHilSubmitClient, HIL_DEV_CONFIG } from '@bizfirst/hil-core';
import type { IHilSubmitClient } from '@bizfirst/hil-core';
import { HilContext } from '../context/HilContext';
import { useHilStore } from '../store/hilStore';

export interface HilProviderProps {
  children: ReactNode;
  submitClient?: IHilSubmitClient;
  currentUserID?: string;
}

export function HilProvider({ children, submitClient, currentUserID }: HilProviderProps): React.JSX.Element {
  const pushEvent = useHilStore((s) => s._pushEvent);
  const resolveEvent = useHilStore((s) => s._resolveEvent);
  const client = submitClient ?? (HIL_DEV_CONFIG.isDummyEnabled ? new DevMockHilSubmitClient() : new FetchHilSubmitClient());

  const engineRef = useRef<HilEngine | null>(null);
  if (!engineRef.current) {
    engineRef.current = new HilEngine({
      onEventAdded: (envelope) => {
        console.log('[HIL] Envelope validated and accepted:', {
          hilType: envelope.HilType,
          engageSessionID: envelope.Session.EngageSessionID,
        });
        pushEvent(envelope);
      },
      onEventResolved: (envelope) => {
        console.log('[HIL] Envelope resolved (removed from queue):', envelope.Session.EngageSessionID);
        // CorrelationID travels with the resolved envelope so the store can tell this turn
        // apart from a same-EngageSessionID revival that has already replaced `active` — see
        // the isMatch comment in hilStore.ts's `_resolveEvent`.
        resolveEvent(envelope.Session.EngageSessionID, envelope.Session.CorrelationID);
      },
      onQueueEmpty: () => {
        console.log('[HIL] Queue empty — closing overlay.');
        useHilStore.getState().closeOverlay();
      },
      onValidationError: (errors, raw) => {
        console.warn('[HIL] Envelope REJECTED by validator — will not display:', errors, raw);
      },
    });
  }

  const pushToEngine = (raw: unknown) => {
    engineRef.current?.push(raw);
  };

  return (
    <HilContext.Provider
      value={{
        engine: engineRef.current,
        submitClient: client,
        currentUserID,
        pushToEngine,
      }}
    >
      {children}
    </HilContext.Provider>
  );
}
